import { useSessionStore } from '@features/auth';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSalesforceSyncApi } from '@features/contacts/hooks/useSalesforceSyncApi.ts';
import useSalesforceApi from '@features/Settings/hooks/useSalesforce.Api.ts';
import {
  type ApiResponse, LogLevels,
  type SalesforceAccount,
  type SalesforceContact,
  type SalesforceContactsProps,
  type SalesforceLists,
  type SalesforceReport
} from '@types';
import type { ContactInfoDto, ContactListDto } from '@features/contacts';
import { IntegrationsContactTypes } from '@features/Settings/enums/IntegrationsEnums.ts';
import useWizardStore from '../../wizard/store/useWizardStore.ts';
import { useTranslation } from 'react-i18next';
import { toastNoAccountWarning } from '@utils/intregrationsUIHelpers.ts';
import { writeErrorLog } from '@lib/logger/logError.ts';
import { writeInfoLog } from '@lib/logger/logInfo.ts';
import { writeWarningLog } from '@lib/logger/logWarning.ts';
import { paintToast } from '@lib/logger/paintToast.ts';

function convertSalesforceListsToLists (
  lists: SalesforceLists[],
  organizationId: string
): ContactListDto[] {
  return lists.map((lists) => {
    const { id, name, attributes: { type } } = lists;
    return {
      id: String(id),
      organizationId,
      title: `${name}`,
      tendrilId: String(id),
      type: IntegrationsContactTypes.Salesforce,
      isFolder: type === 'Folder',
    };
  });
}

function convertSalesforceReportsToLists (
  lists: SalesforceReport[],
  organizationId: string
): ContactListDto[] {
  return lists.map((lists) => {
    const { id, name } = lists;
    return {
      id: String(id),
      organizationId,
      title: `${name}`,
      tendrilId: String(id),
      type: IntegrationsContactTypes.Salesforce,
      isFolder: false,
    };
  });
}

function convertSalesforceContactToTendrilContact (
  contacts: SalesforceContact[]
): ContactInfoDto[] {

  return contacts.map((contact) => {
    const contactListId = contact.salesforceId ?? '';
    const name = `${contact.firstname !== '' ? contact.firstname : ''} ${contact.lastname !== '' ? contact.lastname : ''}`;
    return {
      accountName: contact.company  !== '' ? contact.company : '',
      contactListId,
      email: contact.email !== '' ? contact.email : '',
      id: '',
      jobTitle: contact.title !== '' ? contact.title : '',
      name,
      phone: contact.phone,
      crmId: '',
      notes: '',
      notesAgent: '',
      notesSDR: '',
      errorMessages: [],
    };
  });
}

interface UseSalesforceViewModelReturn {
  salesforceSearch: (query: string)=> Promise<ContactListDto[]>
  retrieveSalesforceAccounts:()=> Promise<string | SalesforceAccount[]>
  retrieveSalesforceLists: () => Promise<ContactListDto[] | undefined>
  retrieveSalesforceReports: (folderName: string) => Promise<ContactListDto[] | undefined>
  retrieveSalesforceContacts: (nonce: string, listId: string) => Promise<ContactInfoDto[] | undefined>
  debugData: string
  nonce: string
}

export function useSalesforceViewModel (): UseSalesforceViewModelReturn {
  const { t } = useTranslation();
  const { setSalesforceURL, setSalesforceNonce, salesforceNonce } = useWizardStore();
  const currentUser = useSessionStore((sessionState) => sessionState.currentUser);
  const organizationId = String(currentUser?.organizationId ?? '').trim();
  const userId = String(currentUser?.userId ?? '').trim();
  const nonceRef = useRef<string>('');
  const debugData = useRef<string>('');
  const [accountsAreLinked, setAccountsAreLinked] = useState(true);

  const { getConnectedAccounts }  = useSalesforceApi();
  const { searchSalesforceObjects, getSalesforceLists, getSalesforceReports, getSalesforceContacts } = useSalesforceSyncApi();
  const SALESFORCE_LIST_ID_LENGTH = 18;
  const SALESFORCE_REPORT_ID_PREFIX = '00O';

  const salesforceSearch = async (query: string):Promise<ContactListDto[]> => {
    const { data } = await searchSalesforceObjects(userId, organizationId, query);
    if (data === undefined) {
      await writeErrorLog('Error while searching Salesforce objects.',
        salesforceSearch.name,
        { organizationId, userId, query }
      );
      return [];
    }

    return convertSalesforceListsToLists(data, organizationId);
  };

  const retrieveSalesforceAccounts = async (): Promise<string | SalesforceAccount[]> => {
    const { data: accountsResponse, error: accountsError } = await getConnectedAccounts(userId, organizationId);

    if (accountsResponse === undefined) {
      return accountsError?.message ?? 'Error while getting Salesforce accounts.';
    }


    if (accountsResponse.length === 0) {
      await writeWarningLog(
        t('contacts.toast.noSalesforceAccounts', { lng: 'en' }),
        retrieveSalesforceAccounts.name,
        { accountsResponse }
      );
      paintToast(LogLevels.WARNING, t('contacts.toast.noSalesforceAccounts'));
    }

    return accountsResponse;
  };

  const retrieveSalesforceLists = useCallback(async (): Promise<ContactListDto[] | undefined> => {
    if (!accountsAreLinked) {
      await toastNoAccountWarning(t('contacts.toast.noIntegration'));
      return;
    }

    const { data: lists, error: contactsError } = await getSalesforceLists(userId, organizationId);
    if (lists === undefined || lists.length === 0) {
      await writeErrorLog(contactsError?.message ?? 'Error while getting Salesforce contacts.',
        'useSalesforceViewModel.retrieveSalesforceLists',
        { organizationId, userId }
      );
      return undefined;
    }

    const convertedLists = convertSalesforceListsToLists(lists, organizationId);

    await writeInfoLog(
      'Salesforce lists retrieved successfully',
      'useSalesforceViewModel.retrieveSalesforceLists',
      { count: lists.length }
    );

    return convertedLists;
  }, [getConnectedAccounts, getSalesforceLists, organizationId, userId]);

  const retrieveSalesforceReports = useCallback(async (folderName: string): Promise<ContactListDto[] | undefined> => {
    if (!accountsAreLinked) {
      return;
    }

    const { data: reports } = await getSalesforceReports(folderName, organizationId, userId, salesforceNonce);

    if (reports === undefined || reports.length === 0) {
      return [];
    }

    const convertedLists = convertSalesforceReportsToLists(reports, organizationId);

    await writeInfoLog(
      'Salesforce lists retrieved successfully',
      'useSalesforceViewModel.retrieveSalesforceLists',
      { count: reports.length }
    );
    return convertedLists;
  }, [getSalesforceReports, organizationId, userId]);

  const retrieveSalesforceContacts = useCallback(async (nonce: string, listId: string): Promise<ContactInfoDto[] | undefined> => {

    if (
      !accountsAreLinked ||
      listId.length !== SALESFORCE_LIST_ID_LENGTH || // A Salesforce id has 18 characters, so a 24-byte MongoId is never sent.
      !listId.startsWith(SALESFORCE_REPORT_ID_PREFIX) // Folders ('00l') and reports ('00O') share that length though 00l is not valid for this endpoint.
    ) {
      return;
    }

    const contactsData: ApiResponse<SalesforceContactsProps>  = await getSalesforceContacts(organizationId, userId, salesforceNonce, listId);

    if (contactsData.data === undefined) {
      return undefined;
    }

    debugData.current =  JSON.stringify(contactsData.data);

    const contactSales = convertSalesforceContactToTendrilContact(contactsData.data.contacts);

    if (contactsData.data.contacts.length === 0) {
      await writeInfoLog('No prospects found for this sequence step.',
        'useSalesforceViewModel.retrieveSalesforceContacts',
        { nonce }
      );
      return [];
    }

    await writeInfoLog(
      'Salesforce prospects retrieved successfully',
      'useSalesforceViewModel.retrieveSalesforceContacts',
      { count: 10 }
    );

    return contactSales;
  }, [getSalesforceContacts]);

  async function setSalesforceParams (): Promise<void> {
    const { data: accountsResponse, error: accountsError } = await getConnectedAccounts(userId, organizationId);

    if (accountsResponse === undefined || accountsResponse.length === 0) {
      await writeErrorLog(
        accountsError?.message ?? 'Error while getting Salesforce accounts.',
        'useSalesforceViewModel.setSalesforceParams',
        { organizationId, userId }
      );
      return undefined;
    }

    setSalesforceURL(accountsResponse[0].instanceUrl);
    setSalesforceNonce(accountsResponse[0].nonce);
  }


  useEffect(()=>{
    void setSalesforceParams();
  }, []);

  useEffect(() => {
    setAccountsAreLinked(nonceRef.current === '' || nonceRef.current === undefined || nonceRef.current === null);
  }, [salesforceNonce]);


  return {
    salesforceSearch,
    retrieveSalesforceAccounts,
    retrieveSalesforceLists,
    retrieveSalesforceReports,
    retrieveSalesforceContacts,
    debugData: debugData.current,
    nonce: nonceRef.current
  };
}